import { type ReactNode, useState } from "react";

import { getFloorById, useAppSelector } from "@lib/store";
import { useRequiredContext } from "@lib/utils";
import { NoMapPlaceholder } from "@molecules";

import { HaNMapFrame, type HaNTabMapLayer } from "../../map/HaNMapFrame";
import { HaNMapLoading } from "../../map/HaNMapLoading";
import { HaNErrorBoundary } from "../../shared/HaNErrorBoundary";
import { HaNContext } from "../HaNContext";
import { HaNMapStageContext } from "./HaNMapStageContext";

// Next to the sidebar on wide screens, on phones it fills the page in the map view.
// Holds the page's one map, which the active tab gives its layer.
export const HaNMapStage = ({ children }: { children: ReactNode }) => {
  const { building, floor } = useRequiredContext(HaNContext);
  const hasMap = useAppSelector(state => !!floor.floorId && !!getFloorById(state, floor.floorId)?.hasMap);
  const [tabLayer, setTabLayer] = useState<HaNTabMapLayer | null>(null);

  // Until the building is settled it can still change, so there's no floor to show yet, not even "no map"
  const stageFloorId = building.isSettled ? floor.floorId : null;
  const mapFloorId = hasMap ? stageFloorId : null;

  return (
    <div className="relative col-start-2 row-[1/-1] overflow-hidden bg-beige-100 max-sm:col-start-1 max-sm:row-[4/6] max-sm:group-data-[mobile-view=list]/han:hidden">
      <HaNErrorBoundary area="MapStage">
        <HaNMapStageContext.Provider value={{ floorId: mapFloorId, setTabLayer }}>
          {mapFloorId && (
            <HaNMapFrame
              floorId={mapFloorId}
              tabLayer={tabLayer}
            />
          )}

          {building.isSettled && !mapFloorId && <NoMapPlaceholder />}

          <HaNMapLoading />

          {/* Kept on a floor without a map, so the floor selector can still move to one that has */}
          {stageFloorId && children}
        </HaNMapStageContext.Provider>
      </HaNErrorBoundary>
    </div>
  );
};
